import { Dictionary } from 'lodash';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { ProjectId, Team } from 'api/models';
import { toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';
import {
  getTeamsInAccountQueryKey,
  useUpdateTeamNameMutation,
} from 'queries/placeholder';
import { updateProjectPlaceholder } from 'stores/memberships';

interface Props {
  teamLookup: Dictionary<Team>;
  onTeamUpdate: (team: Team) => void;
}

interface ReturnProps {
  teamIdToBeUpdated?: number;
  closeEditTeamModal: () => void;
  onEditTeam: (teamId: number) => void;
  updateTeamName: (team: Team) => void;
}

export const useTeamCellActions = ({
  teamLookup,
  onTeamUpdate,
}: Props): ReturnProps => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const [teamIdToBeUpdated, setTeamIdToBeUpdated] = useState<number>();

  const closeEditTeamModal = () => {
    setTeamIdToBeUpdated(undefined);
  };

  const onEditTeam = (teamId: number) => {
    setTeamIdToBeUpdated(teamId);
  };

  const successTexts = {
    title: useTranslation('placeholder.update.success.title'),
    message: useTranslation('placeholder.update.success.message'),
  };

  const errorTexts = {
    title: useTranslation('placeholder.update.error.title'),
    message: useTranslation('placeholder.update.error.message'),
  };

  const { mutate: updateTeam } = useUpdateTeamNameMutation();

  const updateTeamName = (team: Team) => {
    const { teamId, teamName, roleId, projectId } = team;

    if (projectId && roleId) {
      onTeamUpdate(team);
      updateTeam(
        { id: teamId, name: teamName },
        {
          onSuccess: (newTeam) => {
            toastService({
              ...successTexts,
            }).success();
            queryClient.setQueryData<Team[]>(
              getTeamsInAccountQueryKey(),
              (oldValues = []) => [
                ...oldValues.filter(
                  ({ teamId: tId }) => tId !== newTeam.teamId,
                ),
                newTeam,
              ],
            );
            dispatch(
              updateProjectPlaceholder({
                projectId: ProjectId(projectId),
                team,
              }),
            );
          },
          onError: () => {
            toastService({
              ...errorTexts,
            }).error();
            if (teamIdToBeUpdated) {
              onTeamUpdate(teamLookup[teamIdToBeUpdated]);
            }
          },
        },
      );
      setTeamIdToBeUpdated(undefined);
    }
    setTeamIdToBeUpdated(undefined);
  };

  return {
    teamIdToBeUpdated,
    closeEditTeamModal,
    onEditTeam,
    updateTeamName,
  };
};
